import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"
import type { ComponentProps } from "react"
import { cn } from "@/lib/utils.js"

/**
 * Progressive disclosure for a list of things, one open at a time.
 *
 * Radix rather than a `useState` and a conditional: the contract a disclosure
 * owes its reader is `aria-expanded`, a control that points at the region it
 * governs, and space and enter both working — and half of that is worse than
 * none.
 *
 * **The header is deliberately not the trigger.** Everything a person needs in
 * order to triage lives in the header — a status, a summary sentence, the
 * figures that explain the grade — and wrapping that in a `<button>` would be
 * invalid markup and would collapse all of it into one unreadable accessible
 * name. The trigger is its own small control, and it says what it opens.
 */
export const Accordion = AccordionPrimitive.Root

export function AccordionItem({
  className,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      className={cn("rounded-lg border bg-card text-card-foreground shadow-xs", className)}
      {...props}
    />
  )
}

/**
 * `asChild` on the header, so the outline of the page is not littered with a
 * heading per disclosure control. The heading a source deserves is its name,
 * and it is already in the card.
 */
export function AccordionTrigger({
  className,
  children,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header asChild>
      <div className="flex">
        <AccordionPrimitive.Trigger
          className={cn(
            "group inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            className,
          )}
          {...props}
        >
          {children}
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180"
          />
        </AccordionPrimitive.Trigger>
      </div>
    </AccordionPrimitive.Header>
  )
}

export function AccordionContent({
  className,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      className={cn("focus-visible:outline-none", className)}
      {...props}
    />
  )
}
